import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BrandLogo } from "./BrandLogo";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { User, Bell, Tag } from "lucide-react";

const LINKS = [["Offerte", "/offerte"], ["Come funziona", "/come-funziona"]];

function NotifBell() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const unread = items.filter((n) => !n.read).length;

  const load = () => api.get("/account/notifications").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      try { await api.post("/account/notifications/read"); } catch {}
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} data-testid="notif-bell" aria-label="Notifiche"
        className="relative w-9 h-9 grid place-items-center rounded-full text-white/80 hover:bg-white/10 transition-colors">
        <Bell size={18} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#FFC400] text-[#0B0B0D] text-[10px] font-bold grid place-items-center" data-testid="notif-badge">{unread}</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto rounded-2xl bg-[#141419] border border-white/10 shadow-2xl p-2 z-50" data-testid="notif-panel">
          <p className="px-3 py-2 text-xs font-semibold text-white/50 uppercase tracking-wide">Notifiche</p>
          {items.length === 0 ? (
            <p className="px-3 py-6 text-sm text-white/50 text-center" data-testid="notif-empty">Nessuna notifica</p>
          ) : (
            items.map((n) => (
              <button key={n.id} onClick={() => { setOpen(false); if (n.link) nav(n.link); }} data-testid={`notif-item`}
                className="w-full text-left flex gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 transition-colors">
                <span className="w-8 h-8 shrink-0 rounded-lg bg-[#FFC400]/15 grid place-items-center text-[#FFC400]"><Tag size={15} /></span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-white line-clamp-2">{n.title}</span>
                  <span className="block text-xs text-white/60 truncate">{n.body}</span>
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export function PortalNav() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isCustomer = user && user.role === "customer";
  return (
    <header className="sticky top-0 z-40 bg-[#0B0B0D]/80 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
        <Link to="/" data-testid="ch-logo"><BrandLogo name="Pickfood" size={40} /></Link>
        <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-white/70">
          {LINKS.map(([l, h]) => (
            <Link key={h} to={h} className="hover:text-white transition-colors" data-testid={`nav-${h.slice(1)}`}>{l}</Link>
          ))}
          <a href="/diventa-partner" target="_blank" rel="noopener noreferrer" data-testid="nav-diventa-partner"
            className="partner-pill inline-flex items-center gap-1.5 rounded-full bg-[#FFC400] text-[#0B0B0D] font-bold px-4 py-1.5 hover:bg-[#E6AC00] transition-colors">
            <span className="partner-spark w-1.5 h-1.5 rounded-full bg-[#0B0B0D]" /> Diventa partner
          </a>
        </nav>
        <div className="flex items-center gap-2">
          {isCustomer ? (
            <>
              <NotifBell />
              <Button variant="ghost" className="rounded-full text-sm text-white hover:bg-white/10 hover:text-white" onClick={() => nav("/account")} data-testid="nav-account">
                <User size={15} className="mr-1.5" />Il mio account
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" className="rounded-full text-sm text-white hover:bg-white/10 hover:text-white" onClick={() => nav("/account/login")} data-testid="nav-login">Accedi</Button>
              <Button className="rounded-full bg-[#FFC400] hover:bg-[#E6AC00] text-[#0B0B0D] text-sm font-semibold" onClick={() => nav("/registrati")} data-testid="nav-register">Registrati</Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
